@use 'common' as *;
@use '@dixa/lib-shared-tokens/scss' as *;

$image-max-width: 500px;
$stuck: null;

.message {
  background: var(--dixa-color-neutral-000);
  padding: var(--dixa-size-spacer-300);
  border-radius: var(--dixa-size-border-radius-100);
  display: flex;
  position: relative;
  transition: opacity 3s ease-in-out;
  break-inside: avoid;

  &Agent {
    background: var(--dixa-color-neutral-100);
  }

  &Own {
    background: var(--dixa-color-neutral-100);
  }

  &Internal {
    background-color: var(--dixa-color-orange-100);
    border: 1px solid var(--dixa-color-orange-200);
  }

  &Simple {
    padding-left: 56px;
    padding-top: 16px;
  }

  &Failed {
    box-shadow: inset 0 0 1px 1px var(--dixa-color-red-500);
  }

  &Loading {
    animation: pulse 3.5s infinite ease-in-out;

    &::after {
      animation: loader 4s infinite linear;
      content: '';
      display: block;
      height: 2px;
      background: linear-gradient(
        to right,
        transparent 20%,
        var(--dixa-color-purple-500) 50%,
        transparent 80%
      );
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      border-radius: 0 0 6px 6px;
      background-size: 200%;
      background-position: 50%;
    }
  }

  &Active {
    z-index: 1;
  }

  &Stuck {
    $stuck: & !global;
  }
}

.avatarWrap {
  width: 32px;
  min-width: 32px;
  margin-right: 8px;
}

.messageWrap {
  flex: 1;
  align-self: flex-start;
  min-width: 0;
}

.messageContentWrap {
  overflow-y: hidden;
}

.name {
  margin-bottom: var(--dixa-size-spacer-250);
  font-weight: var(--dixa-typography-weight-semibold);
  font-size: var(--dixa-typography-size-300);
  line-height: var(--dixa-typography-line-height-200);
  letter-spacing: var(--dixa-typography-letter-spacing-400);
  color: var(--dixa-typography-color-primary);
  break-after: avoid;

  @media print {
    flex-wrap: wrap;

    .nameMenu {
      flex-basis: 100%;
    }
  }
}

.nameDetails {
  margin-top: calc(-1 * var(--dixa-size-spacer-100));
  margin-bottom: var(--dixa-size-spacer-250);
  break-inside: avoid;
  break-after: avoid;
}

.text {
  font-size: var(--dixa-typography-size-300);
  font-weight: var(--dixa-typography-weight-normal);
  line-height: var(--dixa-typography-line-height-200);
  letter-spacing: var(--dixa-typography-letter-spacing-300);
  color: var(--dixa-typography-color-primary);
  margin: 0;
  word-wrap: break-word;
  orphans: 2;
  widows: 2;

  // stylelint-disable-next-line selector-nested-pattern
  p {
    orphans: 2;
    widows: 2;
  }

  // stylelint-disable-next-line selector-nested-pattern
  img {
    width: auto;
    max-width: min(100%, $image-max-width);
  }

  // stylelint-disable-next-line selector-nested-pattern
  * + p {
    margin-top: 16px;
  }

  // stylelint-disable-next-line selector-nested-pattern
  * + ul {
    margin-top: 16px;
  }

  // stylelint-disable-next-line selector-nested-pattern
  // Inline code
  code {
    font-family:
      'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'source-code-pro', monospace;
    font-size: 0.9em;
    background: var(--dixa-color-neutral-100);
    padding: 2px 6px;
    border-radius: var(--dixa-size-border-radius-100);
    border: 1px solid var(--dixa-color-neutral-200);
  }

  // stylelint-disable-next-line selector-nested-pattern
  pre code {
    padding: 0;
    font-size: inherit;
    color: inherit;
    white-space: pre-wrap;
    background-color: transparent;
    border-radius: 0;
  }

  // There are some links with no text content, but only an href attribute.
  // This is a workaround to show the href as text. This is primarily used
  // (as a hack to hide the link) in the dixa messenger flow with automated
  // messages that contain a link to knowledge base articles.
  a:empty {
    &::before {
      content: attr(href);
      color: var(--dixa-color-neutral-600);
      word-break: break-all;
      text-decoration: dotted underline;
    }
  }

  // stylelint-disable-next-line selector-pseudo-class-no-unknown,selector-nested-pattern
  :global(.emojione) {
    width: 16px;
    height: 16px;
    margin-right: 3px;
    vertical-align: -3px;
  }

  :global(a.message-conversation-link) {
    display: inline-flex;
    background-color: var(--dixa-color-neutral-300);
    padding: var(--dixa-size-spacer-100);
    margin: var(--dixa-size-spacer-100) 0;
    border-radius: var(--dixa-size-border-radius-100);
    color: var(--dixa-color-neutral-800);
    font-size: var(--dixa-typography-size-200);
    font-weight: var(--dixa-typography-weight-semibold);

    &:hover {
      color: var(--dixa-color-neutral-800);
    }
  }
}

.images,
.attachments {
  margin-top: 16px;
  display: flex;
  margin-left: -8px;
  flex-wrap: wrap;
}

.image {
  border-radius: 6px;
  overflow: hidden;

  // stylelint-disable-next-line selector-nested-pattern
  img {
    width: auto;
    max-width: min(100%, $image-max-width);
  }
}

.translatedPill {
  margin-left: var(--dixa-size-spacer-200);
}

.attachment {
  max-width: 100%;
  margin: 4px;
}

.image {
  margin: 4px;
}

// Float the gutter (timestamp) in the message's top-right corner instead of
// reserving the 180px column.
@mixin remove-gutter {
  position: absolute;
  top: var(--dixa-size-spacer-300);
  right: var(--dixa-size-spacer-300);
  min-width: 0;
}

.gutter {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  position: sticky;
  top: 17px;
  min-width: 180px;

  @media print {
    @include remove-gutter;
  }

  &:focus-within {
    // HACK: This ensures that the gutter of adjacent messages does not
    // overlap the current gutter. Dropdowns with high z-index for some reason
    // do not overlap correctly.
    z-index: 1;
  }
}

// Opt-in removal of gutter for when action controls aren't shown,
// so we don't reserve a column of empty space.
.removeGutter {
  @include remove-gutter;
}

.gutterActions {
  @media print {
    display: none !important;
  }
}

.error {
  color: var(--dixa-color-red-500);
  display: inline-flex;
  vertical-align: bottom;
  margin-right: 8px;
}

.status {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.3);
  line-height: 16px;
  font-weight: 600;
  margin-bottom: 8px;
}

.measure,
.goto {
  position: absolute;
  top: -40px;
  left: 0;
  height: 1px;
  width: 1px;
  z-index: 1;
  pointer-events: none;
}

.goto {
  top: -10px;
}

.measure {
  top: 80px;
}

.stuckButton {
  // The transition is set at the call site; see message.tsx.
  opacity: 0;
  pointer-events: none;

  #{$stuck} & {
    pointer-events: all;
    opacity: 1;
  }
}

.timestamp {
  text-align: right;
}

.autoReply {
  margin-left: var(--dixa-size-spacer-100);
}

.autoReplyDivider {
  margin: var(--dixa-size-spacer-200) 0;
}

@keyframes pulse {
  0% {
    opacity: 0.3;
  }

  50% {
    opacity: 0.65;
  }

  100% {
    opacity: 0.3;
  }
}

@keyframes loader {
  0% {
    background-position: 50%;
  }

  25% {
    background-position: 0%;
  }

  50% {
    background-position: 50%;
  }

  75% {
    background-position: 100%;
  }

  100% {
    background-position: 50%;
  }
}

.mentionedLink {
  color: var(--dixa-color-purple-500);
}
